import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { authService } from '../services/api';
import '../styles/Layout.css';
import NotificationBadge from './NotificationBadge';
import { usePermisos } from '../hooks/usePermisos';

const Layout = ({ children, tenantCodigo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { codigo } = useParams();
  const [user, setUser] = useState(authService.getCurrentUser());
  const [tenantInfo, setTenantInfo] = useState(() => {
    const stored = localStorage.getItem('tenant_info');
    return stored ? JSON.parse(stored) : null;
  });
  const { can, hasModule } = usePermisos();

  useEffect(() => {
    if (authService.isAuthenticated() && !user?.permisos) {
      authService.refreshUser()
        .then((data) => {
          if (data) {
            setUser(data);
          }
        })
        .catch(() => {});
    }
  }, [user?.permisos]);

  useEffect(() => {
    const handleStorage = () => {
      setUser(authService.getCurrentUser());
      const infoStr = localStorage.getItem('tenant_info');
      setTenantInfo(infoStr ? JSON.parse(infoStr) : null);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Si no se pasa tenantCodigo como prop, intentar obtenerlo de localStorage o URL
  const activeTenantCodigo = tenantCodigo || codigo || localStorage.getItem('tenant_codigo');
  const planNombre = (tenantInfo?.plan || '').toString().toLowerCase();
  const isPremiumTenant = planNombre.includes('premium');

  // Verificar que el tenant de la URL coincida con el del token
  useEffect(() => {
    if (codigo) {
      const token = localStorage.getItem('token');
      const tenantCodigoFromStorage = localStorage.getItem('tenant_codigo');
      
      if (token && tenantCodigoFromStorage && codigo.toLowerCase() !== tenantCodigoFromStorage.toLowerCase()) {
        // El tenant de la URL no coincide con el del token - forzar logout y re-login
        console.warn(`⚠️ Tenant mismatch en Layout: URL=${codigo}, Token=${tenantCodigoFromStorage}. Forzando logout.`);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant_codigo');
        localStorage.removeItem('tenant_info');
        alert('⚠️ El tenant de la URL no coincide con tu sesión. Por favor, inicia sesión nuevamente.');
        navigate(`/${codigo}/login`);
        return;
      }
    }
  }, [codigo, navigate]);

  useEffect(() => {
    const refreshTenantPlan = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/tenants/${activeTenantCodigo}/info`);
        if (!response.ok) return;
        const info = await response.json();
        localStorage.setItem('tenant_info', JSON.stringify(info));
        setTenantInfo(info);
      } catch (error) {
        console.warn('No se pudo actualizar la información del tenant', error);
      }
    };

    if (activeTenantCodigo) {
      const plan = (tenantInfo?.plan || '').toLowerCase();
      if (!tenantInfo?.plan || (!plan.includes('premium') && !plan.includes('gold'))) {
        refreshTenantPlan();
      }
    }
  }, [activeTenantCodigo, tenantInfo?.plan]);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/tenants/${activeTenantCodigo}/info`);
        if (!response.ok) return;
        const info = await response.json();
        localStorage.setItem('tenant_info', JSON.stringify(info));
        setTenantInfo(info);
      } catch (err) {
        console.warn('No se pudo obtener la información del tenant', err);
      }
    };

    if (activeTenantCodigo) {
      fetchInfo();
    }
  }, [activeTenantCodigo]);

  const handleLogout = () => {
    if (activeTenantCodigo) {
      // Logout para tenant
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant_codigo');
      localStorage.removeItem('tenant_info');
      setUser(null);
      window.location.href = `/${activeTenantCodigo}/login`;
    } else {
      // Logout normal
      authService.logout();
      setUser(null);
    }
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  // Función para obtener la ruta correcta según el contexto (tenant o normal)
  const getRoute = (path) => {
    if (activeTenantCodigo) {
      // Si hay tenant activo, TODAS las rutas deben incluir el código
      return `/${activeTenantCodigo}${path}`;
    }
    // Solo para super admin sin tenant
    return path;
  };

  const isDashboard = location.pathname === '/' || location.pathname === `/${activeTenantCodigo}/dashboard`;

  return (
    <div className="layout">
      <nav className="sidebar">
        <div className="sidebar-header">
          <h3>{tenantInfo?.nombre_negocio || 'Sistema de Préstamos'}</h3>
          {activeTenantCodigo && (
            <span className="tenant-badge">{activeTenantCodigo}</span>
          )}
        </div>

        <div className="user-section">
          <div className="user-avatar">
            <div className="avatar-circle">
              {(user?.nombre_completo || user?.username)?.charAt(0)?.toUpperCase()}
            </div>
          </div>
          <div className="user-details">
            <div className="user-name">{user?.nombre_completo || user?.username}</div>
            <div className="user-role">{user?.rol || 'Usuario'}</div>
          </div>
        </div>

        <ul className="sidebar-menu">
          <li>
            <Link to={getRoute('/dashboard')} className={isActive(getRoute('/dashboard'))}>
              <span>📊</span> Panel
            </Link>
          </li>
          {hasModule('clientes') && (
            <li className="menu-group">
              <input type="checkbox" id="menu-clientes-toggle" />
              <label htmlFor="menu-clientes-toggle" className="menu-group-title">
                <span>👥</span> Cliente
              </label>
              <ul className="submenu">
                {can('clientes', 'crear') && <li><Link to={getRoute('/clientes/nuevo')}>Nuevo</Link></li>}
                {can('clientes', 'editar') && <li><Link to={getRoute('/clientes/editar')}>Editar</Link></li>}
                {can('clientes', 'eliminar') && <li><Link to={getRoute('/clientes/eliminar')}>Eliminar</Link></li>}
                {can('clientes', 'ver') && (
                  <>
                    <li><Link to={getRoute('/clientes/lista')}>Ver clientes</Link></li>
                    <li><Link to={getRoute('/clientes/detalle')}>Detalle Cliente</Link></li>
                    <li><Link to={getRoute('/clientes/referencia')}>Referencia</Link></li>
                  </>
                )}
              </ul>
            </li>
          )}
          {can('prestamos', 'ver') && (
            <li>
              <Link to={getRoute('/prestamos')} className={isActive(getRoute('/prestamos'))}>
                <span>💰</span> Prestamo
              </Link>
            </li>
          )}
          {can('pagos', 'ver') && (
            <li>
              <Link to={getRoute('/pagos')} className={isActive(getRoute('/pagos'))}>
                <span>💳</span> Pagos
              </Link>
            </li>
          )}
          {can('excusas', 'ver') && (
            <li>
              <Link to={getRoute('/excusas')} className={isActive(getRoute('/excusas'))}>
                <span>📋</span> Excusas
              </Link>
            </li>
          )}
          {can('rutas', 'ver') && (
            <li>
              <Link to={getRoute('/rutas')} className={isActive(getRoute('/rutas'))}>
                <span>🗺️</span> Rutas
              </Link>
            </li>
          )}
          {can('metas', 'ver') && (
            <li>
              <Link to={getRoute('/metas-comisiones')} className={isActive(getRoute('/metas-comisiones'))}>
                <span>🎯</span> Metas
              </Link>
            </li>
          )}
          {can('reportes', 'ver') && (
            <li>
              <Link to={getRoute('/reportes')} className={isActive(getRoute('/reportes'))}>
                <span>📈</span> Reportes
              </Link>
            </li>
          )}
          {hasModule('garantias') && (
            <li className="menu-group">
              <input type="checkbox" id="menu-garantias-toggle" />
              <label htmlFor="menu-garantias-toggle" className="menu-group-title">
                <span>📦</span> Garantías
              </label>
              <ul className="submenu">
                {can('garantias', 'ver') && <li><Link to={getRoute('/garantias?vista=lista')}>Lista</Link></li>}
                {can('garantias', 'crear') && <li><Link to={getRoute('/garantias?vista=nueva')}>Nueva</Link></li>}
                {can('garantias', 'eliminar') && <li><Link to={getRoute('/garantias?vista=eliminar')}>Eliminar</Link></li>}
                {can('garantias', 'ver') && <li><Link to={getRoute('/garantias?vista=estadisticas')}>Estadísticas</Link></li>}
              </ul>
            </li>
          )}
          {can('documentos', 'ver') && (
            <li>
              <Link to={getRoute('/documentos')} className={isActive(getRoute('/documentos'))}>
                <span>📄</span> Documentos
              </Link>
            </li>
          )}
          {can('finanzas', 'ver') && (
            <li>
              <Link to={getRoute('/finanzas')} className={isActive(getRoute('/finanzas'))}>
                <span>⚖️</span> Finanzas
              </Link>
            </li>
          )}
          {isPremiumTenant && (
            <li>
              <Link to={getRoute('/exchange')} className={isActive(getRoute('/exchange'))}>
                <span>💱</span> Tasa de Cambio
              </Link>
            </li>
          )}
          {can('contabilidad', 'ver') && (
            <li className="menu-group">
              <input type="checkbox" id="menu-contabilidad-toggle" />
              <label htmlFor="menu-contabilidad-toggle" className="menu-group-title">
                <span>🧮</span> Contabilidad
              </label>
              <ul className="submenu">
                <li><Link to={getRoute('/contabilidad')}>Dashboard</Link></li>
                <li><Link to={getRoute('/contabilidad/cuadre-pagos')}>Cuadre Pagos</Link></li>
                <li><Link to={getRoute('/contabilidad/cuadre-rutas')}>Cuadre Rutas</Link></li>
                <li><Link to={getRoute('/contabilidad/calculadora-cuota')}>Calculadora Cuota</Link></li>
              </ul>
            </li>
          )}
          {can('configuracion', 'ver') && (
            <li>
              <Link to={getRoute('/configuracion')} className={isActive(getRoute('/configuracion'))}>
                <span>⚙️</span> Configuración
              </Link>
            </li>
          )}
          {can('whatsapp', 'ver') && (
            <li>
              <Link to={getRoute('/whatsapp')} className={isActive(getRoute('/whatsapp'))}>
                <span>📱</span> WhatsApp
              </Link>
            </li>
          )}
          {can('usuarios', 'ver') && (
            <li>
              <Link to={getRoute('/permisos')} className={isActive(getRoute('/permisos'))}>
                <span>🛡️</span> Permisos
              </Link>
            </li>
          )}
        </ul>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <span>🚪</span> Cerrar Sesión
          </button>
        </div>
      </nav>

      <div className="main-content">
        {!isDashboard && (
          <header className="header">
            <div className="header-left">
              <h1>{tenantInfo?.nombre_negocio || 'Sistema de Préstamos'}</h1>
            </div>
            <div className="header-right">
              <NotificationBadge />
              <span className="user-info">
                👤 {user?.nombre_completo || user?.username}
              </span>
              <button onClick={handleLogout} className="btn btn-outline">
                Cerrar Sesión
              </button>
            </div>
          </header>
        )}

        <main className={isDashboard ? 'content-dashboard' : 'content'}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;